// src/components/circles/ContactCircleActions.tsx
//
// Two small pieces of UI for wiring Circles into the Contacts page:
//
//   <ContactCircleFilter> — dropdown of workspace Circles used to filter
//                          the contact list (applied server-side via
//                          GET /api/contacts?circleId=).
//   <ContactBulkAddToCircle> — modal for adding a set of contacts to a
//                              target Circle via the additive bulk endpoint.
//
// Both components respect the "no Central primitives duplicated" rule:
// they only speak to CRM's own Circle backend and never assume a
// Central Team/Document primitive exists.

import { useMemo, useState } from 'react';
import {
  useAddCircleMembersMutation,
  useGetCirclesQuery,
} from '../../api/circlesApi';
import LoadingSpinner from '../common/LoadingSpinner';
import { useCircleDialogFocus } from './useCircleDialogFocus';
import CircleTypeBadge from './CircleTypeBadge';
import type { Circle } from '../../types/api';

// ---------------------------------------------------------------------------
// Filter
// ---------------------------------------------------------------------------

interface ContactCircleFilterProps {
  workspaceId?: string;
  value: string | null;
  onChange: (circleId: string | null) => void;
  className?: string;
}

export function ContactCircleFilter({
  workspaceId,
  value,
  onChange,
  className,
}: ContactCircleFilterProps) {
  const { data: circles = [], isLoading } = useGetCirclesQuery(
    { workspaceId },
    { skip: !workspaceId },
  );
  // Smart Circles are excluded: this filter works from stored members, and a
  // Smart Circle's members are evaluated from its rules server-side.
  const activeCircles = useMemo(
    () => circles.filter((c) => !c.archivedAt && c.type !== 'smart_segment'),
    [circles],
  );

  return (
    <label className={className}>
      <span className="sr-only">Filter by circle</span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-w-[160px]"
        aria-label="Filter contacts by circle"
        data-testid="contacts-circle-filter"
        disabled={isLoading}
      >
        <option value="">All Circles</option>
        {activeCircles.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * Client-side filter. Returns the subset of `contactIds` that belong to
 * the given Circle. Requires the Circle's `members` array to be present
 * (the CRM circles list endpoint eagerly loads members).
 */
export function contactIdsInCircle(
  circle: Circle | undefined,
): Set<string> | null {
  if (!circle) return null;
  return new Set(
    (circle.members ?? [])
      .map((m) => m.contactId)
      .filter((v): v is string => !!v),
  );
}

// ---------------------------------------------------------------------------
// Bulk add modal
// ---------------------------------------------------------------------------

interface ContactBulkAddToCircleProps {
  workspaceId: string;
  selectedContactIds: string[];
  onClose: () => void;
  onResult: (result: { added: number; skipped: number; circleName: string }) => void;
}

export function ContactBulkAddToCircle({
  workspaceId,
  selectedContactIds,
  onClose,
  onResult,
}: ContactBulkAddToCircleProps) {
  const [addMembers, { isLoading }] = useAddCircleMembersMutation();
  const dialogRef = useCircleDialogFocus(onClose, isLoading);
  const { data: circles = [], isLoading: circlesLoading } = useGetCirclesQuery(
    { workspaceId },
    { skip: !workspaceId },
  );
  const [circleId, setCircleId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const filteredCircles = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return circles
      // Smart Circles take no manual members (their rules decide).
      .filter((c) => !c.archivedAt && c.type !== 'smart_segment')
      .filter((c) =>
        needle ? c.name.toLowerCase().includes(needle) : true,
      );
  }, [circles, query]);

  const targetCircle = circles.find((c) => c.id === circleId);
  const existingContactIds = useMemo(
    () => contactIdsInCircle(targetCircle) ?? new Set<string>(),
    [targetCircle],
  );
  const newContactIds = selectedContactIds.filter((id) => !existingContactIds.has(id));
  const dupCount = selectedContactIds.length - newContactIds.length;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    if (!targetCircle) {
      setErrorMessage('Pick a circle first.');
      return;
    }
    if (newContactIds.length === 0) {
      setErrorMessage('All selected contacts are already in this circle.');
      return;
    }
    try {
      const response = await addMembers({
        circleId: targetCircle.id,
        members: newContactIds.map((contactId) => ({ contactId })),
      }).unwrap();
      onResult({
        added: response.added.length,
        skipped: response.skipped.length + dupCount,
        circleName: targetCircle.name,
      });
    } catch (err: any) {
      setErrorMessage(
        err?.data?.error || err?.data?.message || 'Failed to add to circle.',
      );
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-add-title"
    >
      <div ref={dialogRef} tabIndex={-1} className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto focus:outline-none">
        <form onSubmit={handleSubmit}>
          <div className="p-6 border-b">
            <h2 id="bulk-add-title" className="text-lg font-semibold text-gray-900">
              Add {selectedContactIds.length} contact
              {selectedContactIds.length === 1 ? '' : 's'} to a circle
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              A contact can belong to multiple circles. Contacts already in the selected circle will be skipped.
            </p>
          </div>
          <div className="p-6 space-y-3">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search circles..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              aria-label="Search circles"
            />
            <div
              className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto"
              role="listbox"
              aria-label="Choose target circle"
              data-testid="bulk-add-circle-list"
            >
              {circlesLoading ? (
                <div className="p-6 flex justify-center"><LoadingSpinner /></div>
              ) : filteredCircles.length === 0 ? (
                <p className="p-4 text-sm text-gray-500">No circles found.</p>
              ) : (
                filteredCircles.map((c) => (
                  <label
                    key={c.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="radio"
                      name="target-circle"
                      value={c.id}
                      checked={circleId === c.id}
                      onChange={() => setCircleId(c.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                      <div className="mt-0.5 flex items-center gap-2">
                        <CircleTypeBadge type={c.type} />
                      </div>
                    </div>
                  </label>
                ))
              )}
            </div>
            {targetCircle && dupCount > 0 && (
              <p className="text-xs text-gray-600" data-testid="bulk-add-dup-notice">
                {dupCount} of the selected contact{dupCount === 1 ? ' is' : 's are'} already in
                <span className="font-medium"> {targetCircle.name}</span> — they will be skipped.
              </p>
            )}
            {errorMessage && (
              <p role="alert" className="text-sm text-red-600">
                {errorMessage}
              </p>
            )}
          </div>
          <div className="p-6 border-t flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !circleId}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
              data-testid="bulk-add-submit"
            >
              {isLoading ? 'Adding…' : 'Add to Circle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
